<script setup lang="ts">
import { useField } from 'vee-validate'
import { isString } from 'es-toolkit'
import { useInjectedId } from '~/composables/useInjectedId'
import { getZxcvbn } from '~/utils/zxcvbn'
import { ZxcvbnFactory } from '@zxcvbn-ts/core'

interface FormPasswordProps {
  name: string
  label: string
  placeholder?: string
  autocomplete?: 'new-password' | 'current-password'
}

defineOptions({
  inheritAttrs: false
})

const props = withDefaults(defineProps<FormPasswordProps>(), {
  placeholder: '',
  autocomplete: undefined
})

const { t } = useI18n()

const { value, handleBlur, errors } = useField<string>(props.name, undefined)
const zxcvbnReady = ref(false)

const $form = useInjectedId(props.name)

const color = computed(() => {
  if (errors.value.length > 0) {
    return 'danger'
  } else if (value.value?.length > 0) {
    return 'success'
  }
  return undefined
})

let zxcvbn: ZxcvbnFactory | null = null
const passwordStrength = computed(() => {
  if (props.autocomplete !== 'new-password' || !zxcvbn || !isString(value.value)) {
    return null
  }

  return value.value ? zxcvbn.check(value.value).score : null
})

const passwordIsVisible = ref(false)
const inputType = computed(() => (passwordIsVisible.value ? 'text' : 'password'))
const eyeIcon = computed(() => (passwordIsVisible.value ? 'fa-eye-slash' : 'fa-eye'))

const toggleVisibility = () => {
  passwordIsVisible.value = !passwordIsVisible.value
}

onMounted(async () => {
  if (props.autocomplete === 'new-password') {
    zxcvbn = await getZxcvbn()
    zxcvbnReady.value = true
  }
})
</script>

<template>
  <VField :label :color="color" :name :message="errors.join('\n')">
    <VInput
      :id="$form.elementId.value"
      v-model="value"
      v-bind="$attrs"
      :type="inputType"
      :label
      :placeholder
      :autocomplete
      @blur="handleBlur"
    />

    <template #icon-right>
      <VIcon
        class="right"
        size="small"
        :icon="eyeIcon"
        :aria-label="`components.molecules.form-wrapper.form-password.${passwordIsVisible ? 'hide' : 'show'}-password`"
        @click="toggleVisibility"
      />
    </template>
  </VField>
  <!-- Ported from: https://github.com/apertureless/vue-password-strength-meter -->
  <div v-if="autocomplete === 'new-password'" class="Password__strength-meter">
    <div class="Password__strength-meter--fill" :data-score="passwordStrength" />
  </div>
</template>

<style scoped>
.Password__strength-meter {
  position: relative;
  height: 3px;
  background: #ddd;
  margin: 10px auto 20px;
  border-radius: 3px;
}

.Password__strength-meter::before,
.Password__strength-meter::after {
  content: '';
  height: inherit;
  background: transparent;
  display: block;
  border-color: var(--ui-border-inverted);
  border-style: solid;
  border-width: 0 5px;
  position: absolute;
  width: 20%;
  z-index: 10;
}

.Password__strength-meter::before {
  left: 20%;
}

.Password__strength-meter::after {
  right: 20%;
}

.Password__strength-meter--fill {
  background: transparent;
  height: inherit;
  position: absolute;
  width: 0;
  border-radius: inherit;
  border-color: transparent;
  transition:
    width 0.5s ease-in-out,
    background 0.25s;
}

.Password__strength-meter--fill[data-score='0'] {
  background: darkred;
  width: 20%;
}

.Password__strength-meter--fill[data-score='1'] {
  background: orangered;
  width: 40%;
}

.Password__strength-meter--fill[data-score='2'] {
  background: orange;
  width: 60%;
}

.Password__strength-meter--fill[data-score='3'] {
  background: yellowgreen;
  width: 80%;
}

.Password__strength-meter--fill[data-score='4'] {
  background: green;
  width: 100%;
}
</style>
